import {
  Button,
  FiCheckCircle,
  FiExternalLink,
  Flex,
  Stack,
  Tooltip,
  useDisclosure,
} from '@agrotoken-tech/ui';
import {
  CREDITOR_PLEDGE_STATUSES_ICON,
  DEBTOR_PLEDGE_STATUSES_ICON,
} from '@bcba/frontend-utils';
import { useTranslation } from '@bcba/react-hooks';
import {
  CROP_PLEDGE_USER_ROLES,
  PLEDGE_SECTION_STATUSES,
  PLEDGE_STATUSES,
  PledgeStatus,
} from '@bcba/types';
import { ReactNode } from 'react';
import { useUserContext } from '../../../../contexts/UserContext';
import { useCropPledgeContext } from '../context/CropPledgeContext';

const useCropPledgeDetail = () => {
  const { t } = useTranslation();
  const { userHasPendingChanges, setUserHasPendingChanges } = useUserContext();

  const {
    sectionTitle,
    tabsDataAside,
    cropPledgeStatus,
    cropPledgeStatusBadgeColors,
    role,
  } = useCropPledgeContext();

  const {
    isOpen: isOpenApproveModal,
    onOpen: onOpenApproveModal,
    onClose: onCloseApproveModal,
  } = useDisclosure();

  const {
    isOpen: isOpenRejectModal,
    onOpen: onOpenRejectModal,
    onClose: onCloseRejectModal,
  } = useDisclosure();

  const {
    isOpen: isOpenMarkAsPaidModal,
    onOpen: onOpenMarkAsPaidModal,
    onClose: onCloseMarkAsPaidModal,
  } = useDisclosure();

  const isCreditor = role === CROP_PLEDGE_USER_ROLES.CREDITOR;
  const currentCropPledgeStatuses = cropPledgeStatus?.statuses;

  const cropPledgeStatusIcons = isCreditor
    ? CREDITOR_PLEDGE_STATUSES_ICON
    : DEBTOR_PLEDGE_STATUSES_ICON;

  const showLotsAmountAlert =
    isCreditor &&
    currentCropPledgeStatuses?.some((s) =>
      (
        [
          PLEDGE_STATUSES.PENDING_CREDITOR_APPROVAL,
          PLEDGE_STATUSES.PENDING_CREDITOR_LOAD,
          PLEDGE_STATUSES.PENDING_DEBTOR_DOCUMENTATION_LOAD,
          PLEDGE_STATUSES.PENDING_BOLSA_LOAD,
          PLEDGE_STATUSES.PENDING_REGISTRATION,
        ] as PledgeStatus[]
      ).includes(s),
    );

  const lotsAmountQty = cropPledgeStatus?.totalEstimatedProductionValueInUsd;
  const lotsAmountAlertDesc = t(
    'cropPledge.creditor.lotsAmountAlert.description',
  )
    .replace('{{currency}}', 'USD')
    .replace(
      '{{amount}}',
      Number(lotsAmountQty ?? 0).toLocaleString('es-AR') ?? '0',
    );

  const isPledgeRegistered = currentCropPledgeStatuses?.includes(
    PLEDGE_STATUSES.REGISTERED,
  );
  const showMarkAsPaidButton = isCreditor && isPledgeRegistered;

  const isCropPledgeInApprovableState =
    currentCropPledgeStatuses?.includes(
      PLEDGE_STATUSES.PENDING_CREDITOR_APPROVAL,
    ) ?? false;

  const isPendingRelease = currentCropPledgeStatuses?.includes(
    PLEDGE_STATUSES.PENDING_RELEASE,
  );
  const isReleased = currentCropPledgeStatuses?.includes(
    PLEDGE_STATUSES.RELEASED,
  );
  const showSeeInBlockchainButton =
    isPledgeRegistered || isPendingRelease || isReleased;

  const onSeeInBlockchain = () => {
    window.open(
      `${process.env.REACT_APP_BLOCKCHAIN_EXPLORER_BASE_URL}${cropPledgeStatus?.url}`,
      '_blank',
    );
  };

  const seeInBlockchainButton = showSeeInBlockchainButton ? (
    <Button
      variant={'secondary'}
      leftIcon={<FiExternalLink />}
      onClick={onSeeInBlockchain}
    >
      {t('common.seeInBlockchain')}
    </Button>
  ) : null;

  let headerActions: ReactNode;
  if (isCreditor) {
    const isFarmInfoApproved =
      cropPledgeStatus?.sections.farm.status ===
      PLEDGE_SECTION_STATUSES.APPROVED;
    const isDebtorInfoApproved =
      cropPledgeStatus?.sections.debtor.status ===
      PLEDGE_SECTION_STATUSES.APPROVED;
    const isCreditorInfoApproved =
      cropPledgeStatus?.sections.creditor.status ===
      PLEDGE_SECTION_STATUSES.APPROVED;
    const approveActionIsDisabled =
      !isFarmInfoApproved || !isDebtorInfoApproved || !isCreditorInfoApproved;

    const approveButton = (
      <Button
        variant="primary"
        isDisabled={approveActionIsDisabled}
        onClick={onOpenApproveModal}
      >
        {t('cropPledge.actions.approve')}
      </Button>
    );

    const markAsPaidButton = (
      <Button
        variant={'primary'}
        leftIcon={<FiCheckCircle />}
        onClick={onOpenMarkAsPaidModal}
      >
        {t('cropPledge.actions.markAsPaid')}
      </Button>
    );

    headerActions = (
      <Stack direction="row" gap={4}>
        {isCropPledgeInApprovableState && (
          <>
            {approveActionIsDisabled ? (
              <Tooltip
                hasArrow
                label={t(
                  'cropPledge.actions.approve.tooltip.pendingSections.text',
                )}
                title={t(
                  'cropPledge.actions.approve.tooltip.pendingSections.text',
                )}
                placement="bottom"
                bg="white"
                color="content-secondary"
                size="sm"
                minW={'auto'}
              >
                <Flex>{approveButton}</Flex>
              </Tooltip>
            ) : (
              approveButton
            )}
            <Button variant="destructive" onClick={onOpenRejectModal}>
              {t('cropPledge.actions.reject')}
            </Button>
          </>
        )}
        {seeInBlockchainButton}
        {showMarkAsPaidButton && markAsPaidButton}
      </Stack>
    );
  } else {
    headerActions = (
      <Stack direction="row" gap={4}>
        {seeInBlockchainButton}
      </Stack>
    );
  }

  return {
    userHasPendingChanges,
    setUserHasPendingChanges,
    sectionTitle,
    tabsDataAside,
    cropPledgeStatusIcons,
    cropPledgeStatusBadgeColors,
    headerActions,
    cropPledgeStatus,
    role,
    isOpenRejectModal,
    onCloseRejectModal,
    isOpenApproveModal,
    onCloseApproveModal,
    isOpenMarkAsPaidModal,
    onCloseMarkAsPaidModal,
    showLotsAmountAlert,
    lotsAmountAlertDesc,
  };
};

export default useCropPledgeDetail;
